/* eslint-disable @typescript-eslint/no-shadow */

import { forwardRef, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { isEmpty, isNonNullish } from 'remeda'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import { useCategoriesGrid } from '@components/CategoriesGrid/CategoriesGridProvider'
import {
  CategoryHeader,
  CategoryHeaderSkeleton,
} from '@components/CategoryHeader/CategoryHeader'
import type {
  CollectionBannerProps,
  CollectionMessageData,
  Product,
} from '@velasca/types'
import { CollectionTextSection } from '@velasca/ui/collection-text-section'
import * as S from './styles'
import type { ApparelCollectionCommonProps } from './types'

interface ApparelCollectionPresentationalProps
  extends ApparelCollectionCommonProps {
  banner?: CollectionBannerProps
  products?: Product[] | null
  canLoadMore?: boolean
  onLoadMore: () => void
  loading?: boolean
  collectionTopMessageData?: CollectionMessageData
  showCollectionBannerOnMobile?: boolean
  productsCount: number
  hasHeader?: boolean
}

export const ApparelCollectionPresentational = forwardRef<
  HTMLDivElement,
  ApparelCollectionPresentationalProps
>(
  (
    {
      showBanner = false,
      showSingleVariant = false,
      loading = false,
      skeletonLength = 6,
      productsCount,
      products = [],
      banner,
      showProductType,
      className = '',
      canLoadMore,
      onLoadMore,
      shouldLoadAllProducts,
      collectionTopMessageData,
      areCollectionCardImagesWornApparel,
      showCollectionBannerOnMobile,
      hasHeader = true,
    }: ApparelCollectionPresentationalProps,
    ref,
  ) => {
    const { frontImage: frontImageToShow } = useCategoriesGrid()
    const [loadMoreRef, inView] = useInView({
      rootMargin: `100000px 0px 1000px 0px`,
      triggerOnce: true,
    })

    useEffect(() => {
      if (!shouldLoadAllProducts && inView && canLoadMore && !loading) {
        onLoadMore()
      }
    }, [inView, canLoadMore, shouldLoadAllProducts, loading, onLoadMore])

    if (isNonNullish(products) && isEmpty(products) && !loading) {
      return null
    }

    return (
      <div className={className}>
        {collectionTopMessageData && !isEmpty(collectionTopMessageData) && (
          <CollectionTextSection
            title={collectionTopMessageData.title}
            upperTitle={collectionTopMessageData.upperTitle}
            paragraph={collectionTopMessageData.paragraph}
          />
        )}
        <S.Grid
          ref={ref}
          fixedHeight={loading || (!shouldLoadAllProducts && canLoadMore)}
          totalProductsLength={productsCount}
        >
          {showBanner && (loading || banner) && (
            <div
              // eslint-disable-next-line react/no-unknown-property
              css={`
                display: ${showCollectionBannerOnMobile ? 'block' : 'none'};

                @media screen and (min-width: 768px) {
                  grid-column: span 2 / span 2;
                  display: block;
                  align-self: flex-start;
                }

                @media screen and (min-width: 992px) {
                  grid-column: span 3 / span 3;
                }
              `}
            >
              {hasHeader &&
                (loading && isEmpty(products ?? []) ? (
                  <CategoryHeaderSkeleton />
                ) : (
                  <CategoryHeader
                    title={banner?.title || ''}
                    description={banner?.description}
                    as="h2"
                  />
                ))}
            </div>
          )}
          {products?.map((product, i, products) => {
            // If there is a load more, in order to NOT show last product that could have the wrong numbers of Variant Dots
            return (
              (shouldLoadAllProducts ||
                !canLoadMore ||
                products.length !== i + 1) && (
                <S.CardContent key={`${product?.title}-${i}`}>
                  <ApparelProduct
                    frontImageToShow={frontImageToShow}
                    showSingleVariant={showSingleVariant}
                    showProductType={showProductType}
                    showBrandingOnWornImage={areCollectionCardImagesWornApparel}
                    {...product}
                  />
                </S.CardContent>
              )
            )
          })}
          {/* Show some loading cards when:
          - I am loading in infinite scrolling mode and I can load some more
          - When I am loading all products at the same time and the status is loading.

          I have the number of products from liquid, so I can load every single skeleton. The -1 is because I am hiding a loaded product. So I need to put a skeleton more.
           */}
          {((!shouldLoadAllProducts && canLoadMore) || loading) &&
            [...Array(skeletonLength).keys()].map((id) => (
              <S.CardContent
                key={id}
                {...(id === 0 && products && !isEmpty(products)
                  ? { ref: loadMoreRef }
                  : {})}
              >
                <ApparelProduct
                  loading
                  frontImageToShow={frontImageToShow}
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                />
              </S.CardContent>
            ))}
        </S.Grid>
      </div>
    )
  },
)
